import { useEffect, useState } from "react";
import { Download, Search, StickyNote } from "lucide-react";
import api, { authHeaders, downloadCsv } from "@/lib/api";
import { APP_STATUSES, APP_STATUS_LABELS } from "@/lib/constants";

export default function AppsTab({ endpoint, kind }) {
  const [apps, setApps] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState({});

  const load = (query = "", st = "") => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (st) params.set("status", st);
    api.get(`${endpoint}?${params.toString()}`, { headers: authHeaders() }).then(({ data }) => setApps(data || []));
  };

  useEffect(() => {
    load();
  }, []);

  const patch = (id, body) => {
    api.patch(`${endpoint}/${id}`, body, { headers: authHeaders() }).then(({ data }) => {
      setApps((prev) => prev.map((a) => (a._id === data._id ? data : a)));
      setNotes((n) => ({ ...n, [id]: "" }));
    });
  };

  const isPartner = kind === "partner";

  return (
    <div data-testid={`admin-apps-tab-${kind}`}>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            className="input-brox !pl-11"
            placeholder="Caută după firmă, contact, telefon..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(q, status)}
            data-testid={`admin-apps-search-${kind}`}
          />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); load(q, e.target.value); }} className="input-brox sm:max-w-xs" data-testid={`admin-apps-status-filter-${kind}`}>
          <option value="">Toate statusurile</option>
          {APP_STATUSES.map((s) => (
            <option key={s} value={s}>{APP_STATUS_LABELS[s]}</option>
          ))}
        </select>
        <button
          onClick={() => downloadCsv(`${endpoint}/export`, `brox-${kind === "partner" ? "parteneri" : "furnizori"}.csv`)}
          className="inline-flex min-h-[52px] shrink-0 items-center justify-center gap-2 border border-white/15 px-5 font-display text-base font-bold uppercase tracking-wider text-paper transition-colors hover:border-copper hover:text-copper"
          data-testid={`admin-apps-export-btn-${kind}`}
        >
          <Download className="h-4 w-4" />
          EXPORTĂ CSV
        </button>
      </div>

      {apps === null && <p className="text-sm text-muted-foreground">Se încarcă...</p>}
      {apps !== null && apps.length === 0 && (
        <p className="border border-dashed border-white/15 p-10 text-center text-sm text-muted-foreground" data-testid={`admin-apps-empty-${kind}`}>
          Nicio cerere încă.
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {apps?.map((a) => (
          <div key={a._id} className="border border-white/10 bg-ink-900 p-6" data-testid={`admin-app-${kind}-${a._id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-xl font-bold uppercase tracking-wide text-paper">{a.company}</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {a.contact_person} · <a href={`tel:${a.phone}`} className="text-copper">{a.phone}</a> · {a.email}
                </p>
              </div>
              <span className="font-mono text-[10px] text-muted-foreground">
                {new Date(a.created_at).toLocaleDateString("ro-RO", { dateStyle: "medium" })}
              </span>
            </div>

            <div className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {isPartner ? (
                <>
                  <p><span className="label-brox !mb-0.5">Reședință</span>{a.county_city || "—"}</p>
                  <p><span className="label-brox !mb-0.5">Zone acoperite</span>{a.areas_covered || "—"}</p>
                  <p><span className="label-brox !mb-0.5">Lucrări</span>{a.work_types || "—"}</p>
                  <p><span className="label-brox !mb-0.5">Lucrători</span>{a.workers_count || "—"}</p>
                  <p><span className="label-brox !mb-0.5">Experiență</span>{a.experience_years || "—"}</p>
                  <p><span className="label-brox !mb-0.5">Disponibilitate</span>{a.availability || "—"}</p>
                  {a.company_details && <p className="sm:col-span-2"><span className="label-brox !mb-0.5">Detalii firmă</span>{a.company_details}</p>}
                  {a.additional_info && <p className="sm:col-span-2"><span className="label-brox !mb-0.5">Informații suplimentare</span>{a.additional_info}</p>}
                </>
              ) : (
                <>
                  <p><span className="label-brox !mb-0.5">Locații</span>{a.locations || "—"}</p>
                  <p><span className="label-brox !mb-0.5">Zone livrare</span>{a.delivery_areas || "—"}</p>
                  <p className="sm:col-span-2"><span className="label-brox !mb-0.5">Categorii materiale</span>{a.material_categories || "—"}</p>
                  {a.additional_info && <p className="sm:col-span-2"><span className="label-brox !mb-0.5">Informații suplimentare</span>{a.additional_info}</p>}
                </>
              )}
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="label-brox">Status</label>
                <select value={a.status} onChange={(e) => patch(a._id, { status: e.target.value })} className="input-brox" data-testid={`admin-app-status-select-${kind}-${a._id}`}>
                  {APP_STATUSES.map((s) => (
                    <option key={s} value={s}>{APP_STATUS_LABELS[s]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-brox">Notă internă</label>
                <div className="flex gap-2">
                  <input
                    className="input-brox flex-1"
                    placeholder="Adaugă notă..."
                    value={notes[a._id] || ""}
                    onChange={(e) => setNotes((n) => ({ ...n, [a._id]: e.target.value }))}
                    data-testid={`admin-app-note-input-${kind}-${a._id}`}
                  />
                  <button
                    onClick={() => notes[a._id]?.trim() && patch(a._id, { note: notes[a._id].trim() })}
                    className="btn-primary !px-4 !min-h-[48px] text-base"
                    data-testid={`admin-app-note-add-${kind}-${a._id}`}
                  >
                    <StickyNote className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {(a.notes || []).length > 0 && (
              <div className="mt-3 space-y-2">
                {a.notes.map((n, i) => (
                  <p key={i} className="border border-white/10 bg-ink-950 px-4 py-2.5 text-sm text-paper/80">
                    {n.text}
                    <span className="ml-2 font-mono text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" })}</span>
                  </p>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
